import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  CircularProgress,
  Box,
  Avatar,
  Card,
  CardActions,
  Alert,
} from '@mui/material';
import {
  GoogleAuthProvider,
  signInWithPopup,
  onAuthStateChanged,
  signOut,
} from 'firebase/auth';
import { getDocs, collection, getFirestore } from 'firebase/firestore';
import GoogleIcon from '@mui/icons-material/Google';
import LockIcon from '@mui/icons-material/Lock';
import { auth, firebaseApp } from '../providers/firebase';

const db = getFirestore(firebaseApp);

const checkFirestoreAccess = async () => {
  try {
    await getDocs(collection(db, 'sync'));
    return true;
  } catch (error) {
    console.error('Error reading Firestore sync collection:', error);
    return false;
  }
};

const Login = () => {
  const [loading, setLoading] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    // Listen for changes in authentication state
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        // If a user is detected, verify Firestore access
        checkFirestoreAccess().then((hasAccess) => {
          if (hasAccess) {
            navigate('/');
          } else {
            setError('You are not allowed to login. Admin access required.');
            signOut(auth);
          }
        });
      }
      // Mark that Firebase has finished checking the auth state
      setAuthChecked(true);
    });
    return () => unsubscribe();
  }, [navigate]);

  const handleGoogleLogin = async () => {
    setLoading(true);
    setError('');
    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      console.log('Signed in user:', result.user);

      const hasAccess = await checkFirestoreAccess();
      if (!hasAccess) {
        setError('You are not allowed to login. Admin access required.');
        await signOut(auth);
        setLoading(false);
        return;
      }

      navigate('/');
    } catch (error) {
      console.error('Google Sign-In Error:', error);
      setError('An error occurred during sign in. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // While waiting for Firebase to determine the auth state, show a spinner
  if (!authChecked) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'url(https://source.unsplash.com/featured/1600x900)',
        backgroundSize: 'cover',
      }}
    >
      <Card sx={{ minWidth: 320, padding: '2em', textAlign: 'center' }}>
        <Avatar sx={{ bgcolor: 'secondary.main', margin: 'auto' }}>
          <LockIcon />
        </Avatar>
        <h2>Sign In</h2>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <CardActions>
          <Button
            variant="contained"
            color="primary"
            fullWidth
            startIcon={<GoogleIcon />}
            onClick={handleGoogleLogin}
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Sign in with Google'}
          </Button>
        </CardActions>
      </Card>
    </Box>
  );
};

export default Login;
